import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapAnchor, SchematicMapSurface, useMapProjection } from "@/shared/map";
import type { MapCamera } from "@/shared/map/mapCamera";
import type { GlobeCluster, GlobeRegion } from "./globeTypes";
import { useMapDeps } from "./mapDeps";
import { isLastKnown, regionBounds, screenGroups } from "./publicMapModel";
import { RegionAmap } from "./RegionAmap";
import { MergedPin, SpotPin } from "./SpotMarker";
import { ResidentRoute } from "../actors/ResidentActor";
import { routeEnds, routePoint, routeProgress, type ScreenPoint } from "../actors/actorModel";

/**
 * 放大到地区以后的那一层：真实身份头像、同一处几位的人数角标；点一位出短卡，点几位出名单（页面管）。
 * 底图（c84a 0620Z/0701Z：访客可以用公开的 JS 地图链）：
 * - 默认高德（./RegionAmap）：GET /map/config（公开）→ world_map/amapLoader → 站点根 /_AMapService；坐标先 WGS-84 → GCJ-02；
 * - 三种情况降级成 shared/map 的示意底图（SchematicMapSurface，realBasemap={false}，只读用、不改它），图上写“示意图”：
 *   没配置（/map/config 说不可用或读不到）、高德加载失败、这台设备建不起 WebGL（高德 JS API 2.0 要 WebGL）。
 *   降级时点位仍由同一份公开坐标（WGS-84）按 Web Mercator 投影。
 */
export type RegionBasemap = "pending" | "amap" | "schematic";
export type DegradeReason = "no-config" | "load-failed" | "no-webgl";

const CONFIG_KEY = ["pets", "public-map-config"] as const;

/**
 * 示意图在四周原有的 56 之外再让出的地方（UI2-WORLD-MAP-4CD-01，cand-110 评审 4c「按钮压住最上面的点」），合计上 176、右 64、下 100、左 56：
 * 上面原来是顶栏第二行（回总览、地名人数胶囊，top 62、高 44，320 宽折两行到约 158）。UI2-CITY-LAYER-117 起地区层顶栏只有一行（「‹ 地球」、地名人数，
 * 底边约 54～66），这里不跟着收：/world 右边的缩放键往下到约 180，头像针从坐标往上占 60（角标）～79（“上次在这”），收了最上面的点会贴着缩放键；
 * 下面是署名和新鲜事条（底边往上 86）。点的坐标都落在合计的四边以内。
 */
export const REGION_MAP_INSETS = { top: 120, right: 8, bottom: 44, left: 0 } as const;

/**
 * 取景框：在这些点的外接框四周各加三成跨度（至少 FRAME_MIN_PAD_DEG），只用来定示意图的镜头。
 * 下限原来是 0.01°：一格（0.01°）里的几位错开以后（UI-REVIEW110-04），一片只有一格时镜头宽出整格两倍，几位隔 20～35px、合成一个，放大三级才分开；
 * 收到 0.003° 后一格的视野约 1.4km，第一眼就分得开。跨度大于约 0.033° 的片区按三成算，不受影响。
 */
export const FRAME_MIN_PAD_DEG = 0.003;
export function framed(points: Array<{ lat: number; lng: number }>): Array<{ lat: number; lng: number }> {
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const padLat = Math.max(FRAME_MIN_PAD_DEG, (Math.max(...lats) - Math.min(...lats)) * 0.3);
  const padLng = Math.max(FRAME_MIN_PAD_DEG, (Math.max(...lngs) - Math.min(...lngs)) * 0.3);
  return [
    { lat: Math.min(...lats) - padLat, lng: Math.min(...lngs) - padLng },
    { lat: Math.max(...lats) + padLat, lng: Math.max(...lngs) + padLng },
  ];
}

/** 这一片的标识（高德镜头只在同一片里接着看）。 */
export function regionKeyOf(region: GlobeRegion): string {
  return `${region.center.lat.toFixed(3)},${region.center.lng.toFixed(3)}|${region.latSpan}x${region.lngSpan}`;
}

/** “刚才发生在…一带”：片区格子中心一圈虚线，旁边写地点（只到片区）。 */
export function EventSpot({ label }: { label: string }) {
  return (
    <span className="ps-pmap-spot" data-testid="event-spot">
      <span className="ps-pmap-spot__ring" aria-hidden="true" />
      <span className="ps-pmap-spot__label" role="status">
        刚才发生在{label}
      </span>
    </span>
  );
}

/** 城市层的时刻（UI2-CITY-ACTORS-117）：在路上的几位按计划进度放，计划时刻只到分钟，所以每分钟走一次；不排帧，没有动画。 */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/**
 * 一位一处的标记、在路上这一程（6796 的 routeEnds：两头的片区都说得清）：两头的片区中心，和按计划走到几成（routeProgress，读不出时为 null，
 * routePoint 让 TA 停在起点那头）。说不清的照旧放在公开坐标上。
 * 只管同一座城里的这一程（两头片区的城市都说得清、而且相同）：出远门的那一程，线会拉出城市镜头几百公里、TA 也跟着走出镜头，
 * 照旧放在后端按路线估的公开坐标上、不画线（设计评审中 8）。
 */
export function roadOf(cluster: GlobeCluster, nowMs: number) {
  if (cluster.residents.length !== 1) return null;
  const resident = cluster.residents[0];
  const ends = routeEnds(resident);
  if (!ends || !ends.from.city || ends.from.city !== ends.to.city) return null;
  return { ends, progress: routeProgress(resident.move, nowMs) };
}

/** 屏幕坐标定位的叠加物：和 MapAnchor 同一种定位（同一个类、同一个 translate），只是位置已经在同一投影上算好。 */
function ScreenAnchor({ at, z, children }: { at: ScreenPoint; z: number; children: ReactNode }) {
  return (
    <div className="ps-map__anchor" style={{ transform: `translate(${at.x}px, ${at.y}px)`, zIndex: z }}>
      {children}
    </div>
  );
}

/**
 * 示意图上的头像（第三轮）：按此刻的屏幕位置，会互相压住的几处（头像、人数角标、“上次在这”的牌子都算）合成一个计数标记，
 * 画在人最多那一处的真实坐标上（不取平均、不挪坐标），点开列这几位；拉近分得开了就各自显示。高德那一路不在这里（它自己能放大）。
 * 在路上的几位（UI2-CITY-ACTORS-117）：两片区中心之间画一条示意虚线（6796 的 ResidentRoute，只到片区），TA 放在线上计划走到的那一点，
 * 互相压住也按这一点算。
 * - 线只画给单独显示的那几位：合成进计数标记的，线说明不了什么，只剩一截孤线（设计评审高 3）；
 * - 合成标记照旧画在真实坐标上：组里第一处不在路上的那一处；全是在路上的，画在第一处的公开坐标上（设计评审中 4）。
 */
function SchematicPins({ clusters, openKey, onActivate, nowMs, paused }: { clusters: readonly GlobeCluster[]; openKey: string | null; onActivate: (cluster: GlobeCluster, from: HTMLElement) => void; nowMs: number; paused: boolean }) {
  const { project } = useMapProjection();
  const roads = new Map<GlobeCluster, { from: ScreenPoint; to: ScreenPoint; at: ScreenPoint }>();
  for (const cluster of clusters) {
    const road = roadOf(cluster, nowMs);
    if (!road) continue;
    const from = project(road.ends.from);
    const to = project(road.ends.to);
    roads.set(cluster, { from, to, at: routePoint(from, to, road.progress) });
  }
  const placed = (at: { lat: number; lng: number }) => roads.get(at as GlobeCluster)?.at ?? project(at);
  const groups = screenGroups(clusters, placed, (cluster) => cluster.residents.length === 1 && isLastKnown(cluster.residents[0]));
  const alone = new Set(groups.filter((group) => group.length === 1).map((group) => group[0]));
  return (
    <>
      {[...roads]
        .filter(([cluster]) => alone.has(cluster))
        .map(([cluster, road]) => (
          <ResidentRoute key={`route:${cluster.key}`} from={road.from} to={road.to} />
        ))}
      {groups.map((group) => {
        const anchor = group[0];
        if (group.length === 1) {
          const z = anchor.key === openKey ? 3 : 1;
          const pin = <SpotPin cluster={anchor} expanded={anchor.key === openKey} onActivate={onActivate} paused={paused} />;
          const road = roads.get(anchor);
          return road ? (
            <ScreenAnchor key={anchor.key} at={road.at} z={z}>
              {pin}
            </ScreenAnchor>
          ) : (
            <MapAnchor key={anchor.key} at={{ lat: anchor.lat, lng: anchor.lng }} z={z}>
              {pin}
            </MapAnchor>
          );
        }
        const home = group.find((cluster) => !roads.has(cluster)) ?? anchor;
        const merged: GlobeCluster = { key: `merged:${group.map((c) => c.key).join("|")}`, lat: home.lat, lng: home.lng, residents: group.flatMap((c) => c.residents) };
        return (
          <MapAnchor key={merged.key} at={{ lat: home.lat, lng: home.lng }} z={merged.key === openKey ? 3 : 2}>
            <MergedPin cluster={merged} expanded={merged.key === openKey} onActivate={onActivate} />
          </MapAnchor>
        );
      })}
    </>
  );
}

export function RegionLayer({
  clusters,
  region,
  focus,
  openKey,
  onActivate,
  spot = null,
  quietControls = false,
  paused = false,
  camera,
}: {
  clusters: readonly GlobeCluster[];
  region: GlobeRegion;
  /** 回来接着看（示意图时）：上次点开的那一处，首次渲染时以它为中心 */
  focus: { lat: number; lng: number } | null;
  openKey: string | null;
  onActivate: (cluster: GlobeCluster, from: HTMLElement) => void;
  /** 从新鲜事点进来：发生在哪一带（片区格子中心），标出来 */
  spot?: { lat: number; lng: number; label: string } | null;
  quietControls?: boolean;
  /** 页面有短卡、名单、就近面板或抽屉盖着地图：小角色停在当下（UI2-CITY-ACTORS-117，接 ResidentActor 的 paused） */
  paused?: boolean;
  /** 只挪镜头的口子（UI-WORLD-LIVING-117-2b，shared/map/mapCamera）：就近面板放不下生活照时，地图用它把被点的那一位往上挪；接给高德或示意图，不给就没有 */
  camera?: Ref<MapCamera>;
}) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsButton = useRef<HTMLButtonElement>(null);
  const deps = useMapDeps();
  const [webgl] = useState(() => deps.webgl());
  const config = useQuery({ queryKey: CONFIG_KEY, queryFn: ({ signal }) => deps.loadConfig(signal), staleTime: Infinity, retry: false, enabled: webgl });
  const [loadFailed, setLoadFailed] = useState(false);
  const bounds = useMemo(() => framed(regionBounds(clusters, region)), [clusters, region]);
  const nowMs = useMinuteClock();

  const degrade: DegradeReason | null = !webgl ? "no-webgl" : config.isError || (config.data && !config.data.available) ? "no-config" : loadFailed ? "load-failed" : null;
  const basemap: RegionBasemap = degrade ? "schematic" : config.data ? "amap" : "pending";

  return (
    <div className="ps-pmap__layer" data-layer="region" data-basemap={basemap} data-degrade={degrade ?? undefined} data-testid="region-layer" data-quiet-controls={quietControls ? "true" : undefined} data-map-tools={toolsOpen ? "open" : "closed"} onKeyDown={event => { if (quietControls && toolsOpen && event.key === "Escape" && event.target instanceof HTMLElement && (event.target === toolsButton.current || event.target.closest(".ps-map__controls"))) { event.preventDefault(); event.stopPropagation(); setToolsOpen(false); toolsButton.current?.focus(); } }}>
      {quietControls && basemap === "schematic" ? <button ref={toolsButton} type="button" className="ps-discovery-region-tools" aria-label="地图显示选项" aria-expanded={toolsOpen} data-map-interactive onClick={() => setToolsOpen(value => !value)}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="3" fill="var(--c-surface)" stroke="currentColor" strokeWidth="1.5"/><circle cx="15" cy="17" r="3" fill="var(--c-surface)" stroke="currentColor" strokeWidth="1.5"/></svg></button> : null}
      {basemap === "amap" && config.data ? (
        <RegionAmap
          config={config.data}
          loadMap={deps.loadMap}
          clusters={clusters}
          region={region}
          regionKey={regionKeyOf(region)}
          openKey={openKey}
          onActivate={onActivate}
          onFail={() => setLoadFailed(true)}
          spot={spot}
          nowMs={nowMs}
          paused={paused}
          camera={camera}
        />
      ) : basemap === "schematic" ? (
        <SchematicMapSurface bounds={bounds} routes={[]} places={[]} focus={focus} label="示意图 · 位置都是大约片区" realBasemap={false} insets={REGION_MAP_INSETS} focusLabel="回到上次看的那一处" camera={camera}>
          <SchematicPins clusters={clusters} openKey={openKey} onActivate={onActivate} nowMs={nowMs} paused={paused} />
          {spot ? (
            <MapAnchor at={{ lat: spot.lat, lng: spot.lng }} z={0}>
              <EventSpot label={spot.label} />
            </MapAnchor>
          ) : null}
        </SchematicMapSurface>
      ) : (
        <p className="ps-pmap__state ps-pmap__state--chip" role="status">
          正在打开地图…
        </p>
      )}
    </div>
  );
}
